import { unwrapResult } from '@reduxjs/toolkit'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { loadOrderStepTrackingLink } from '@/features/domain/order'
import { selectCalendarRange } from '@/features/domain/ui'
import { useIsUnmounted } from '@/hooks'
import { useAppDispatch } from '@/store'

import { OrdStepId } from '../typings'
import { getOrdStepId } from '../utils'

import {
  hasOrderStepTrackingLink,
  setOrderStepTrackingLink,
} from '../../../../../orderNotificationAtom'

type LoadingStatus = 'loading' | 'failed' | 'ready'

export const useOrderStepTrackingLinkStatus = (
  extendedOrder: uui.domain.client.rm.ExtendedOrderStep,
  orderDate?: string,
) => {
  const {
    orderStep: { id: orderStepId },
  } = extendedOrder

  const dispatch = useAppDispatch()
  const isUnmounted = useIsUnmounted()
  const calendarRange = useSelector(selectCalendarRange)

  const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>(() => 'loading')

  const archivedDate = orderDate ? orderDate : calendarRange.minDate

  const waitingForServer = useRef(false)
  const ordStepId = useMemo(() => getOrdStepId(extendedOrder), [extendedOrder])

  const fetchData = useCallback(
    async (ordStepId: OrdStepId, orderStepId: string, orderDate: string) => {
      waitingForServer.current = true

      const notificationsRequest = await dispatch(
        loadOrderStepTrackingLink({
          ordStepId,
          orderDate,
        }),
      )

      if (isUnmounted()) return

      if (loadOrderStepTrackingLink.fulfilled.match(notificationsRequest)) {
        const trackingLink = unwrapResult(notificationsRequest)

        if (trackingLink) {
          setOrderStepTrackingLink(orderStepId, trackingLink)
        }

        waitingForServer.current = false

        setLoadingStatus('ready')
      } else {
        setLoadingStatus('failed')
      }
    },
    [dispatch, isUnmounted, archivedDate],
  )

  useEffect(() => {
    if (hasOrderStepTrackingLink(orderStepId)) {
      setLoadingStatus('ready')
    } else {
      setLoadingStatus('loading')
      fetchData(ordStepId, orderStepId, archivedDate)
    }
  }, [orderStepId, ordStepId, fetchData, archivedDate])

  return loadingStatus
}
